import { ActivationResponse, ForcePasswordChangeFormData } from "@data-types";
import { SessionStorageProvider, localizationService } from "@state";
import { action, observable, runInAction } from "mobx";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import { getCurrentCulture } from "@utils";
import { UserAuthStore } from "@state/shared";

const loadFailPath = `/${getCurrentCulture()}/app-update`;
// prettier-ignore
const ForcePasswordChangeService = lazy(loadFailPath, ()=>import("@services/membership/force-password-change/ForcePasswordChangeService"));

export class PasswordResetStore {
	userStore: UserAuthStore;

	@observable public isPasswordResetWorking: boolean = false;
	@observable public forcePwdResetData: ActivationResponse | null = null;
	@observable public hasForcePwdResetError: boolean = false;

	constructor(userStore: UserAuthStore) {
		this.userStore = userStore;
	}

	@action.bound
	public async onInitializePasswordReset(): Promise<void> {
		this.isPasswordResetWorking = true;
		if (this.userStore.accountActivation?.activationToken == null) {
			return;
		}

		let forcePwdResetData: ActivationResponse | null = null;
		try {
			forcePwdResetData = await (
				await ForcePasswordChangeService
			).default.getForcePasswordChangeData(
				this.userStore.accountActivation?.activationToken
			);
		} catch (error) {
			runInAction(() => {
				this.hasForcePwdResetError = true;
			});
		}

		runInAction(() => {
			this.forcePwdResetData = forcePwdResetData;
			this.isPasswordResetWorking = false;
		});
	}

	@action.bound
	public async handlePwdForceResetSubmit(
		data: ForcePasswordChangeFormData
	): Promise<void> {
		if (this.userStore.accountActivation == null) {
			return;
		}

		this.isPasswordResetWorking = true;

		const accountActivationModel = {
			recoveryToken: this.userStore.accountActivation.activationToken,
			agencyId: this.userStore.accountActivation.agencyId,
			shopId: this.userStore.accountActivation.shopId,
			password: data.password,
			confirmPassword: data.newPasswordConfirm,
			username: data.userCardNumber,
		};

		try {
			const response = await (
				await ForcePasswordChangeService
			).default.createForcePasswordChange(accountActivationModel);

			await this.userStore.loginStore.resolveUserActivationLogin(
				response.primary,
				response.secondary,
				Number(response.errorCode)
			);
		} catch (error) {
			if (error?.userData) {
				if (error.userData?.activationToken) {
					//show popup with user data
					this.userStore.userDataConfirmationStore.setUserDataToBeConfirmed(
						{
							...error.userData,
							isInternetUser: false,
						}
					);
					this.userStore.accountActivationStore.setUserConfirmationData(
						error.userData
					);
					this.userStore.loginStore.setUserDataConfirmationState(
						true
					);
				} else {
					//show message about shop
					this.userStore.rootStore.notificationStore.error(
						localizationService.t(
							"USER.POPUPS.DATA_CONFIRMATION.USER_DATA_CONFIRMATION_REQUIRED",
							{ 0: error.userData.shopInfo }
						)
					);
				}
			} else {
				this.userStore.rootStore.notificationStore.error(
					localizationService.t(
						`MEMBERSHIP.FORCE_PASSWORD_CHANGE.${error?.message}`
					)
				);
			}
		}

		runInAction(() => {
			this.isPasswordResetWorking = false;
		});
	}

	@action.bound
	public async onCancelPwdForceReset(): Promise<void> {
		this.isPasswordResetWorking = true;

		try {
			this.userStore.removeUserFromLocalStorage();
			SessionStorageProvider.remove("slip");
		} catch (error) {
			this.userStore.rootStore.notificationStore.error(
				localizationService.t(
					"USER.ACCOUNT_SETTINGS.ERR_HANDLING.GENERIC_ERROR"
				)
			);
		}

		runInAction(() => {
			this.isPasswordResetWorking = false;
		});
	}

	@action.bound
	public clearForcePwdError(): void {
		this.hasForcePwdResetError = false;
	}
	@action.bound
	public onDisposePwdReset(): void {
		this.forcePwdResetData = null;
		this.clearForcePwdError();
	}
}
